import ImageUploadCard from '@/components/Form/Shared/ImageUploadCard'
import useUploadFile from '@/hooks/useUploadFile'
import { EMPLOYEE_MARRIAGE_OPTIONS } from '@/modules/Account/components/constants'
import {
    scanIdentity,
    getCities,
    getDitricts,
    getWards,
} from '@/modules/Employee/services'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import React, { useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button, GridHookForm, Typogaphy } from 'vnetwork-ui'
import {
    COUNTRIES,
    EMPLOYEE_GENDER,
    NATIONS,
    RELIGIONS,
} from '../../../constants'
import { getGender, getNationality } from '../functions'
import dayjs from 'dayjs'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface PersonalTabProps {
    formControl: UseFormReturn<any>
    onNext?: () => void
}

const PersonalTab: React.FC<PersonalTabProps> = ({ formControl, onNext }) => {
    const { t } = useTranslation()
    const { id } = useParams()

    const frontInputRef = useRef<HTMLInputElement>(null)
    const backInputRef = useRef<HTMLInputElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [isScanning, setIsScanning] = useState(false)
    const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
    const [isCameraOpen, setIsCameraOpen] = useState(false)
    const [captureType, setCaptureType] = useState<'front' | 'back' | null>(
        null
    )

    // Scan cooldown and attempt tracking
    const [scanAttempts, setScanAttempts] = useState(0)
    const [cooldownTimer, setCooldownTimer] = useState(0)

    // Upload hook for CCCD images
    const { uploadAsync, isUploading, percent } = useUploadFile({
        prefix: 'employees/documents',
        onSuccess: (data) => {
            console.log('Upload successful:', data)
        },
        onError: (error) => {
            toast.error(t('upload-failed'))
            console.error('Upload error:', error)
        },
    })

    // Countdown timer effect
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null

        if (cooldownTimer > 0) {
            interval = setInterval(() => {
                setCooldownTimer((prev) => {
                    if (prev <= 1) {
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        }

        return () => {
            if (interval) clearInterval(interval)
        }
    }, [cooldownTimer])

    // Scan identity mutation
    const { mutateAsync: scanIdentityAsync } = useMutationWithHandler(
        {
            mutationFn: scanIdentity,
        },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'identity-scan-success',
            errorMessage: 'error-occurred',
            onSuccess: async (response) => {
                if (response?.result) {
                    // Update scan attempts and set cooldown
                    setScanAttempts((prev) => prev + 1)
                    setCooldownTimer(30) // 30 second cooldown

                    // Auto-fill form with scanned data
                    const scanData = response.result

                    console.log('Scan Identity Response:', scanData)

                    // Auto-fill from FRONT data
                    // ngày sinh
                    if (scanData.front.date_of_birth)
                        formControl?.setValue(
                            'employee_birthday',
                            dayjs(scanData.front.date_of_birth, 'DD/MM/YYYY')
                        )

                    // nguyên quán
                    if (scanData.front.place_of_origin) {
                        formControl?.setValue(
                            'employee_original_place',
                            scanData.front.place_of_origin
                        )
                        formControl?.setValue(
                            'employee_birthplace',
                            scanData.front.place_of_origin
                        )
                    }

                    // quốc tịch
                    if (scanData.front.nationality) {
                        formControl?.setValue(
                            'employee_nationality',
                            getNationality(scanData.front.nationality)
                        )
                    }

                    // giới tính
                    if (scanData.front.gender) {
                        formControl?.setValue(
                            'employee_gender',
                            getGender(scanData.front.gender)
                        )
                    }

                    // số CCCD
                    if (scanData.front.id_number) {
                        formControl?.setValue(
                            'employee_idno',
                            scanData.front.id_number
                        )
                    }

                    // Auto-fill from BACK data
                    // ngày cấp
                    if (scanData.back.issue_date) {
                        formControl?.setValue(
                            'employee_idno_issued_on',
                            dayjs(scanData.back.issue_date, 'DD/MM/YYYY')
                        )
                    }

                    // nơi cấp
                    if (scanData.back.place_of_issue) {
                        formControl?.setValue(
                            'employee_idno_issued_by',
                            scanData.back.place_of_issue
                        )
                    }

                    // ngày hết hạn
                    if (scanData.front.expiry_date) {
                        formControl?.setValue(
                            'employee_idno_expiry_date',
                            scanData.front.expiry_date
                        )
                    }

                    // Handle nationality and address parsing
                    if (
                        scanData.front.nationality &&
                        (scanData.front.place_of_residence ||
                            scanData.front.place_of_origin)
                    ) {
                        await handleNationality(
                            scanData.front.nationality,
                            scanData.front.place_of_residence ||
                                scanData.front.place_of_origin
                        )
                    }
                }
            },
        }
    )

    const handleImageSelect = (
        type: 'front' | 'back',
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        if (!file) return
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error(t('file-size-too-large'))
            return
        }

        if (type === 'front') {
            const currentAttachments =
                formControl?.watch('documents.0.attachments') || []
            formControl?.setValue('documents.0.attachments', [
                ...currentAttachments,
                file,
            ])
        } else {
            const currentAttachments =
                formControl?.watch('documents.1.attachments') || []
            formControl?.setValue('documents.1.attachments', [
                ...currentAttachments,
                file,
            ])
        }

        // Clear input value to allow re-selecting the same file
        event.target.value = ''
    }

    const openCamera = async (type: 'front' | 'back') => {
        try {
            setCaptureType(type)
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment', // Use back camera by default
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
            })
            setCameraStream(stream)
            setIsCameraOpen(true)

            if (videoRef.current) {
                videoRef.current.srcObject = stream
            }
        } catch (error) {
            console.error('Error accessing camera:', error)
            toast.error(t('camera-access-error'))
        }
    }

    const handleNationality = async (nationality: string, street: string) => {
        if (nationality?.toLowerCase() === 'việt nam') {
            formControl?.setValue('employee_country', 'VN')
            formControl?.setValue('employee_street', street)
            formControl?.setValue('employee_temporary_street', street)

            // Parse address from street to get city/district/ward info
            try {
                // Get cities for Vietnam
                const cities = await getCities('VN')

                // Try to find matching city from the street address
                const matchedCity = cities.find(
                    (city) =>
                        street
                            ?.toLowerCase()
                            .includes(city.label.toLowerCase()) ||
                        city.label
                            .toLowerCase()
                            .includes(
                                street?.toLowerCase().split(',')[0]?.trim() ||
                                    ''
                            )
                )

                if (matchedCity) {
                    formControl?.setValue('employee_city', matchedCity.value)
                    formControl?.setValue(
                        'employee_temporary_city',
                        matchedCity.value
                    )

                    // Get districts for the matched city
                    const districts = await getDitricts(
                        matchedCity.value as string
                    )
                    const matchedDistrict = districts.find(
                        (district) =>
                            street
                                ?.toLowerCase()
                                .includes(district.label.toLowerCase()) ||
                            district.label
                                .toLowerCase()
                                .includes(
                                    street
                                        ?.toLowerCase()
                                        .split(',')[1]
                                        ?.trim() || ''
                                )
                    )

                    if (matchedDistrict) {
                        formControl?.setValue(
                            'employee_district',
                            matchedDistrict.value
                        )
                        formControl?.setValue(
                            'employee_temporary_district',
                            matchedDistrict.value
                        )

                        // Get wards for the matched city and district
                        const wards = await getWards({
                            state: matchedCity.value as string,
                            district: matchedDistrict.value as string,
                        })
                        const matchedWard = wards.find(
                            (ward) =>
                                street
                                    ?.toLowerCase()
                                    .includes(ward.label.toLowerCase()) ||
                                ward.label
                                    .toLowerCase()
                                    .includes(
                                        street
                                            ?.toLowerCase()
                                            .split(',')[2]
                                            ?.trim() || ''
                                    )
                        )

                        if (matchedWard) {
                            formControl?.setValue(
                                'employee_ward',
                                matchedWard.value
                            )
                            formControl?.setValue(
                                'employee_temporary_ward',
                                matchedWard.value
                            )
                        }
                    }
                }
            } catch (error) {
                console.error('Error processing address:', error)
                // Don't show error to user, just continue without auto-filling location
            }
        }
    }

    const closeCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop())
            setCameraStream(null)
        }
        setIsCameraOpen(false)
        setCaptureType(null)
    }

    // Helper function to safely create object URLs
    const safeCreateObjectURL = (file: any): string | null => {
        if (!file) return null

        try {
            // Check if it's already a File or Blob
            if (file instanceof File || file instanceof Blob) {
                return URL.createObjectURL(file)
            }

            // Check if it has a preview property (might be our custom object)
            if (file && typeof file === 'object' && file.preview) {
                return file.preview
            }

            // If it's a string URL, return it directly
            if (typeof file === 'string') {
                return file
            }
        } catch (error) {
            console.warn('Failed to create object URL:', error)
        }

        return null
    }

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current || !captureType) return

        const video = videoRef.current
        const canvas = canvasRef.current
        const context = canvas.getContext('2d')

        if (!context) return

        // Set canvas size to video size
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        // Draw video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height)

        // Convert canvas to blob
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    const file = new File(
                        [blob],
                        `cccd_${captureType}_${Date.now()}.jpg`,
                        {
                            type: 'image/jpeg',
                        }
                    )

                    if (captureType === 'front') {
                        const currentAttachments =
                            formControl?.watch('documents.0.attachments') || []
                        formControl?.setValue('documents.0.attachments', [
                            ...currentAttachments,
                            file,
                        ])
                    } else {
                        const currentAttachments =
                            formControl?.watch('documents.1.attachments') || []
                        formControl?.setValue('documents.1.attachments', [
                            ...currentAttachments,
                            file,
                        ])
                    }

                    closeCamera()
                    toast.success(t('photo-captured'))
                }
            },
            'image/jpeg',
            0.8
        )
    }

    const handleScanIdentity = async () => {
        // Check if maximum attempts reached
        if (scanAttempts >= 5) {
            toast.error(t('maximum-scan-attempts-reached'))
            return
        }

        // Check if still in cooldown
        if (cooldownTimer > 0) {
            toast.error(t('please-wait-seconds', { seconds: cooldownTimer }))
            return
        }

        setIsScanning(true)

        try {
            // Upload both images first
            const frontFiles =
                formControl?.watch('documents.0.attachments') || []
            const backFiles =
                formControl?.watch('documents.1.attachments') || []
            const allFiles = [...frontFiles, ...backFiles]

            if (allFiles.length === 0) {
                toast.error(t('please-select-images'))
                return
            }

            const uploadResult = await uploadAsync(allFiles)

            const frontUpload = uploadResult[0]
            const backUpload = uploadResult[1]

            if (
                frontUpload.status === 'rejected' ||
                backUpload.status === 'rejected'
            ) {
                toast.error(t('upload-failed'))
                return
            }
            if (
                frontUpload.status === 'fulfilled' &&
                backUpload.status === 'fulfilled'
            ) {
                await scanIdentityAsync({
                    employee_id: Number(id),
                    attachments: {
                        front: frontUpload.value?.attachment_file_url || '',
                        back: backUpload.value?.attachment_file_url || '',
                    },
                })
            }
        } catch (error) {
            console.error('Scan process error:', error)
        } finally {
            setIsScanning(false)
        }
    }

    return (
        <div className="space-y-4">
            {/* CCCD Upload Section */}
            <div className="rounded-md p-4 bg-gradient-to-tr from-blue-50 to-blue-100">
                <Typogaphy
                    size="medium"
                    className="font-semibold mb-2 text-gray-900"
                >
                    {t('cccd-upload-scan')}
                </Typogaphy>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    <ImageUploadCard
                        type="front"
                        image={{
                            file: formControl?.watch(
                                'documents.0.attachments'
                            )?.[0],
                            preview: safeCreateObjectURL(
                                formControl?.watch(
                                    'documents.0.attachments'
                                )?.[0]
                            ),
                        }}
                        onSelect={() => frontInputRef.current?.click()}
                        onCapture={() => openCamera('front')}
                        percent={percent}
                    />

                    <ImageUploadCard
                        type="back"
                        image={{
                            file: formControl?.watch(
                                'documents.1.attachments'
                            )?.[0],
                            preview: safeCreateObjectURL(
                                formControl?.watch(
                                    'documents.1.attachments'
                                )?.[0]
                            ),
                        }}
                        onSelect={() => backInputRef.current?.click()}
                        onCapture={() => openCamera('back')}
                        percent={percent}
                    />
                </div>

                <div className="flex justify-end flex-col items-end space-y-2">
                    {/* Scan attempts and cooldown info */}
                    {(scanAttempts > 0 || cooldownTimer > 0) && (
                        <div className="text-sm space-y-1">
                            {scanAttempts > 0 && (
                                <div className="text-gray-600">
                                    {t('scan-attempts-used')}: {scanAttempts}/5
                                </div>
                            )}
                            {cooldownTimer > 0 && (
                                <div className="text-orange-600 font-medium">
                                    {t('cooldown-remaining')}: {cooldownTimer}s
                                </div>
                            )}
                        </div>
                    )}

                    <Button
                        variant="contained"
                        type="button"
                        onClick={handleScanIdentity}
                        disabled={
                            !formControl?.watch(
                                'documents.0.attachments'
                            )?.[0] ||
                            !formControl?.watch(
                                'documents.1.attachments'
                            )?.[0] ||
                            isUploading ||
                            isScanning ||
                            cooldownTimer > 0 ||
                            scanAttempts >= 5
                        }
                        loading={isScanning}
                        className={`min-w-[150px] ${
                            scanAttempts >= 5
                                ? 'bg-red-500 hover:bg-red-600'
                                : cooldownTimer > 0
                                  ? 'bg-orange-500 hover:bg-orange-600'
                                  : scanAttempts > 0
                                    ? 'bg-blue-500 hover:bg-blue-600'
                                    : ''
                        }`}
                    >
                        {isScanning
                            ? t('scanning')
                            : scanAttempts >= 5
                              ? t('scan-limit-reached')
                              : cooldownTimer > 0
                                ? `${t('wait')} ${cooldownTimer}s`
                                : scanAttempts > 0
                                  ? t('scan-again')
                                  : t('scan-identity')}
                    </Button>
                </div>

                {/* Hidden file inputs */}
                <input
                    ref={frontInputRef}
                    type="file"
                    onChange={(e) => handleImageSelect('front', e)}
                    className="hidden"
                />
                <input
                    ref={backInputRef}
                    type="file"
                    onChange={(e) => handleImageSelect('back', e)}
                    className="hidden"
                />
            </div>

            <GridHookForm
                formControl={formControl}
                subForm={true}
                actions={[
                    {
                        type: 'button',
                        variant: 'contained',
                        title: t('employees.next_step'),
                        handler: async () => {
                            formControl?.handleSubmit(onNext as any)()
                        },
                    },
                ]}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data={[
                    {
                        formItemProps: {
                            name: 'employee_email',
                            type: 'textfield',
                            label: t('employees.email') + ' *',
                            placeholder: t('employees.email'),
                        },
                        className: 'md:col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'employee_phone_number',
                            type: 'textfield',
                            label: t('employees.phone') + ' *',
                            placeholder: t('employees.phone'),
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_birthday',
                            type: 'datepicker',
                            label: t('employees.birthday') + ' *',
                            placeholder: t('employees.birthday'),
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_gender',
                            type: 'select',
                            label: t('employees.gender'),
                            placeholder: t('employees.gender'),
                            options: EMPLOYEE_GENDER(t),
                            allowClear: false,
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_marriage',
                            type: 'select',
                            label: t('employees.marriage'),
                            placeholder: t('employees.marriage'),
                            options: EMPLOYEE_MARRIAGE_OPTIONS(t),
                            allowClear: false,
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_birthplace',
                            type: 'textfield',
                            label: t('employees.birthplace'),
                            placeholder: t('employees.birthplace'),
                        },
                        className: 'md:col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'employee_nationality',
                            type: 'select',
                            label: t('nationality'),
                            placeholder: t('nationality'),
                            options: COUNTRIES,
                            allowClear: false,
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_religion',
                            type: 'select',
                            label: t('religion'),
                            placeholder: t('religion'),
                            options: RELIGIONS,
                            allowClear: false,
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_nation',
                            type: 'select',
                            label: t('nation'),
                            placeholder: t('nation'),
                            options: NATIONS,
                            allowClear: false,
                        },
                    },
                    {
                        formItemProps: {
                            name: 'employee_original_place',
                            type: 'textfield',
                            label: t('original-place'),
                            placeholder: t('original-place'),
                        },
                        className: 'md:col-span-2',
                    },
                    {
                        renderEditComponent: () => (
                            <p className="md:col-span-2 mt-2 text-sm font-semibold text-gray-900">
                                {t('employee:emergency_contact')}{' '}
                                <span className="text-red-500">*</span>
                            </p>
                        ),
                        className: 'md:col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_homies.0.contact_relationship',
                            type: 'textfield',
                            label: t('employee:contact_relationship') + ' *',
                            placeholder: t('employee:contact_relationship'),
                        },
                    },
                    {
                        formItemProps: {
                            name: 'contact_homies.0.contact_phone',
                            type: 'textfield',
                            label: t('employee:contact_phone') + ' *',
                            placeholder: t('employee:contact_phone'),
                        },
                    },
                ]}
            />

            {/* Camera Modal */}
            {isCameraOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <Typogaphy size="medium" className="font-semibold">
                                {t('capture-photo')} -{' '}
                                {captureType === 'front'
                                    ? t('cccd-front-side')
                                    : t('cccd-back-side')}
                            </Typogaphy>
                            <Button
                                variant="text"
                                onClick={closeCamera}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </Button>
                        </div>

                        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-80 object-cover"
                            />
                            <canvas ref={canvasRef} className="hidden" />
                        </div>

                        <div className="flex justify-center gap-4">
                            <Button variant="outlined" onClick={closeCamera}>
                                {t('cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={capturePhoto}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                📷 {t('capture')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PersonalTab
